import { LayoutColumn } from "@/models/user_settings/layout-column.model"
import { LayoutColumnComponents } from "@/enums/models/layout-column-components.enum"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"
import { useUserSettingsStore } from "@/store/user-settings.store"
import moment from "moment/moment"
import { storeToRefs } from "pinia"
import { usePlanningStore } from "@/store/planning.store"
import { getNextWeekday } from "@/helper/time-helper"
import { useAuthStore } from "@/store/auth.store.ts"
import SocketService from "@/services/socket/socket-service.ts"
import { RoleTypes } from "@/enums/role-types.ts"
import type { CommandHandler } from "@/interfaces/socket/socket-handlers.interface.ts"

export class LayoutController {
  private layoutColumnStore = useLayoutColumnsStore()
  private userSettingsStore = useUserSettingsStore()
  private planningStore = usePlanningStore()
  private authStore = useAuthStore()

  private currentFilterCommandHandler: CommandHandler | null = null

  constructor() {
    // User might not have loaded yet, so we add a handler to the authStore
    this.authStore.addActiveUserChangeHandler((user) => {
      if (user && user.knownBusinessEntityContact?.hasRole(RoleTypes.PLANNER)) {
        this.listenToInstructionFinderFilterCommand()
      } else if (this.currentFilterCommandHandler) {
        if (this.currentFilterCommandHandler) {
          SocketService.getInstance().removeCommandHandler(this.currentFilterCommandHandler)
          this.currentFilterCommandHandler = null
        }
      }
    })
  }

  public addColumn(layoutColumnComponent: LayoutColumnComponents, atIndex?: number) {
    const column = this.getDefaultColumn(layoutColumnComponent)
    this.layoutColumnStore.addColumn(column, atIndex)
    this.userSettingsStore.saveUserSettings().then()
    return column
  }

  public removeColumn(columnId: string) {
    this.layoutColumnStore.removeColumn(columnId)

    if (this.layoutColumnStore.layoutColumns.length === 0) {
      this.layoutColumnStore.showLayoutControls = true
      this.layoutColumnStore.addColumn(
        new LayoutColumn({
          component: LayoutColumnComponents.LAYOUT_CONTROLS
        })
      )
    }

    this.userSettingsStore.saveUserSettings().then()
  }

  public hasColumn(layoutColumnComponent: LayoutColumnComponents) {
    return this.layoutColumnStore.hasColumn(layoutColumnComponent)
  }

  public setInitialInstructionColumnDates() {
    const { instructionColumns } = storeToRefs(this.layoutColumnStore)

    let lastday = moment().startOf("day")
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    lastday._f = "YYYY-MM-DD"
    for (const column of instructionColumns.value) {
      if (!column.props) {
        column.props = {}
      }
      column.props.date = lastday.clone()
      lastday = getNextWeekday(lastday)
    }
  }

  private getDefaultColumn(layoutColumnComponent: LayoutColumnComponents) {
    switch (layoutColumnComponent) {
      case LayoutColumnComponents.ORDER_LIST:
        return this.getDefaultOrdersColumn()
      case LayoutColumnComponents.INSTRUCTION_LIST:
        return this.getDefaultInstructionsColumn()
      default:
        return new LayoutColumn({ component: layoutColumnComponent })
    }
  }

  private getDefaultOrdersColumn() {
    return new LayoutColumn(
      new LayoutColumn({
        component: LayoutColumnComponents.ORDER_LIST
      })
    )
  }

  private getDefaultInstructionsColumn() {
    let nextBusinessDay

    const lastDate = this.planningStore.getLastInstructionColumnDate()
    // If there are already IInstructionFilters registered, get the last one and infer the next business day.
    if (lastDate) {
      nextBusinessDay = getNextWeekday(lastDate)
    } else {
      nextBusinessDay = moment() // Default is today (no other columns exist)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      nextBusinessDay._f = "YYYY-MM-DD"
    }
    return new LayoutColumn(
      { component: LayoutColumnComponents.INSTRUCTION_LIST },
      { date: nextBusinessDay }
    )
  }

  private listenToInstructionFinderFilterCommand() {
    this.currentFilterCommandHandler = SocketService.getInstance().addCommandHandler({
      command: "filter-instruction-finder",
      handler: async (data) => {
        const { activeUser } = storeToRefs(this.authStore)

        if (data.userId === activeUser.value!.id) {
          let instructionFinderColumn
          if (!this.hasColumn(LayoutColumnComponents.INSTRUCTION_FINDER)) {
            instructionFinderColumn = this.addColumn(LayoutColumnComponents.INSTRUCTION_FINDER)
          } else {
            instructionFinderColumn = this.layoutColumnStore.layoutColumns.find(
              (column) => column.component === LayoutColumnComponents.INSTRUCTION_FINDER
            )
          }
          if (instructionFinderColumn) {
            if (!instructionFinderColumn.props) {
              instructionFinderColumn.props = {}
            }
            instructionFinderColumn.props.dateStart = moment(data.periodStart)
            instructionFinderColumn.props.dateEnd = moment(data.periodEnd)
            instructionFinderColumn.props.projectId = data.projectId
          }
        }
      }
    })
  }
}
